import React, { useState, useEffect, useRef } from 'react';
import { Block } from '../classes/Block';
import './BlockConfigForm.css';
import { t } from '@lingui/core/macro';
import {
  Select,
  MenuItem,
} from "@mui/material";
import ColorPicker from './ColorPicker';
import ReusableFontPicker, { FontOption } from './ReusableFontPicker';
import FontWeightWidthControls from './FontWeightWidthControls';
import type { CardGroup } from '../utils/cardGroups';
import { useProject } from '../contexts/ProjectContext';
import { trackEvent } from '../utils/analytics';
import {
  VariableFontConfig,
  normalizeVariableFontConfigSteps,
} from '../utils/fontHelpers';

interface BlockConfigFormProps {
  blockNames: string[]; // These are potential names for NEW blocks, from App.tsx's firstColValues
  onSaveBlocks: (blocks: Block[]) => void | Promise<void>;
  onBlockSelect?: (index: number) => void;
  selectedBlockIndex?: number | null;
  existingBlocks: Block[];
  fontOptions: FontOption[];
  isCardsMode: boolean;
  fps: number;
  cardGroups: CardGroup[];
}

const BlockConfigForm: React.FC<BlockConfigFormProps> = ({
  blockNames,
  onSaveBlocks,
  onBlockSelect,
  selectedBlockIndex,
  existingBlocks,
  fontOptions,
  isCardsMode,
  fps,
  cardGroups
}) => {
  const { selectedProject } = useProject();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [activeBlockIndex, setActiveBlockIndex] = useState<number>(0);
  const [collapsedPanes, setCollapsedPanes] = useState<Record<string, boolean>>({
    layout: false,
    label: false,
    value: false,
    card: false
  });

  // Reference to track if blocks were updated from props to prevent feedback loops
  const isUpdatingFromProps = useRef<boolean>(false);

  // Synchronize the internal `blocks` state with the `existingBlocks` prop.
  // This is the primary way blocks (with dataRows) enter the form.
  useEffect(() => {
    console.log('BlockConfigForm: existingBlocks prop received:', existingBlocks);
    const normalizedBlocks: Block[] = existingBlocks.map((block: Block) => {
      const isPlainSingleCellBlockValue: boolean =
        block.isSingleCell && !block.isSongBlock && !block.isCreditsList && !block.isLogoBlock;
      return Block.create({ // Create new instances to ensure no shared refs with App.tsx state directly
        ...block,
        hasSeparateValueFormatting: isPlainSingleCellBlockValue ? true : block.hasSeparateValueFormatting,
        dataRows: block.dataRows || []
      });
    });

    setBlocks((previousBlocks: Block[]) => {
      if (JSON.stringify(previousBlocks) === JSON.stringify(normalizedBlocks)) {
        console.log('BlockConfigForm: Internal blocks seem identical to prop, no update.');
        return previousBlocks;
      }

      console.log('BlockConfigForm: Internal blocks are different from prop, updating internal blocks.');
      isUpdatingFromProps.current = true; // Mark that we're updating from props
      return normalizedBlocks;
    });
    // If existingBlocks is empty, and we have blockNames, this might be an initial setup from App.tsx
    // where App.tsx hasn't yet processed an actual file but wants to show block tabs based on some default names.
    // However, App.tsx is now primarily responsible for creating initial blocks even from just names.
  }, [existingBlocks]);

  // useEffect to automatically save blocks whenever they change internally
  useEffect(() => {
    // Skip saving if the update came from props to prevent feedback loop
    if (isUpdatingFromProps.current) {
      console.log('BlockConfigForm: Skipping save because update came from props');
      isUpdatingFromProps.current = false; // Reset the flag
      return;
    }

    // Only call onSaveBlocks if the blocks array reference itself has changed
    // and it's not an initial setup or an update directly from props.
    // The comparison `JSON.stringify(blocks) !== JSON.stringify(existingBlocks)` helps ensure we save
    // when internal changes make `blocks` different from the last `existingBlocks` received.
    if (JSON.stringify(blocks) !== JSON.stringify(existingBlocks)) {
      // Further check: Ensure that the current `blocks` state is not empty if `existingBlocks` wasn't, 
      // to prevent saving an accidentally cleared state. This is a safeguard.
      if (blocks.length > 0 || (existingBlocks && existingBlocks.length === 0)) {
        console.log('BlockConfigForm: Internal blocks changed, calling onSaveBlocks with:', blocks);
        const result = onSaveBlocks(blocks);
        if (result instanceof Promise) {
          result.catch(error => {
            console.error('Error in onSaveBlocks:', error);
          });
        }
      } else {
        console.log('BlockConfigForm: Internal blocks changed but are empty while existingBlocks were not; suppressing save.', blocks, existingBlocks);
      }
    }
  }, [blocks, onSaveBlocks, existingBlocks]); // Add existingBlocks here to re-evaluate save condition

  // Synchronize the internal active block index with the selected block index from parent
  useEffect(() => {
    if (selectedBlockIndex !== undefined && selectedBlockIndex !== null) {
      setActiveBlockIndex(selectedBlockIndex);
    }
  }, [selectedBlockIndex]);

  const parseFrameValue = (value: unknown): number => {
    const parsedValue: number = Number(value);
    if (!Number.isFinite(parsedValue)) {
      return 0;
    }
    return Math.max(0, Math.floor(parsedValue));
  };

  type BlockType = 'role-name' | 'single-cell' | 'song' | 'credits' | 'logo';

  const getBlockType = (block: Block | undefined): BlockType | 'unknown' => {
    if (!block) return 'unknown';
    if (block.isSongBlock) return 'song';
    if (block.isCreditsList) return 'credits';
    if (block.isLogoBlock) return 'logo';
    if (block.isSingleCell) return 'single-cell';
    return 'role-name';
  };

  const isPlainSingleCellBlock = (block: Block | undefined): boolean => {
    return getBlockType(block) === 'single-cell';
  };

  const handleBlockChange = (index: number, field: keyof Block, value: any) => {
    const blockForAnalytics: Block | undefined = blocks[index] ?? existingBlocks[index];
    trackEvent('block_property_changed', {
      project_id: selectedProject?.id,
      block_id: blockForAnalytics?.id,
      block_name: blockForAnalytics?.name,
      property_name: String(field),
      block_type: getBlockType(blockForAnalytics),
      is_cards_mode: isCardsMode,
    }, {
      dedupeKey: `${selectedProject?.id ?? 'no_project'}:${blockForAnalytics?.id ?? index}:${String(field)}`,
    });

    setBlocks(prevBlocks => {
      const cardSharedFields: Array<keyof Block> = [
        'cardFadeInFrames',
        'cardDurationFullyUpFrames',
        'cardFadeOutFrames',
        'cardSlugFrames',
        'cardOverrideFullWidth',
      ];
      const isCardSharedField: boolean = cardSharedFields.includes(field);
      const selectedBlockId: string | undefined = prevBlocks[index]?.id;
      const activeCardGroup: CardGroup | undefined = selectedBlockId
        ? cardGroups.find((group: CardGroup) => group.blockIds.includes(selectedBlockId))
        : undefined;
      const cardBlockIdSet: Set<string> = new Set(activeCardGroup?.blockIds ?? []);

      const updatedBlocks = prevBlocks.map((block, i) => {
        const shouldUpdateCurrentBlock: boolean = i === index;
        const shouldUpdateCardBlock: boolean = isCardsMode && isCardSharedField && cardBlockIdSet.has(block.id);
        const shouldUpdate: boolean = shouldUpdateCurrentBlock || shouldUpdateCardBlock;

        if (shouldUpdate) {
          // Handle special case for hasSeparateValueFormatting
          const blockUsesForcedValueFormatting: boolean = isPlainSingleCellBlock(block);
          const blockHasSeparateValueFormatting: boolean = blockUsesForcedValueFormatting || block.hasSeparateValueFormatting;
          let newHasSeparateValueFormatting = blockUsesForcedValueFormatting || (
            field === 'hasSeparateValueFormatting'
              ? Boolean(value)
              : block.hasSeparateValueFormatting
          );

          // If we're disabling separate value formatting, copy label formatting to value formatting
          let newValueFontName = block.valueFontName;
          let newValueFontSize = block.valueFontSize;
          let newValueFontWeight = block.valueFontWeight;
          let newValueFontWidth = block.valueFontWidth;
          let newValueAlign = block.valueAlign;
          let newValueFontColor = block.valueFontColor;
          let newValueVerticalAlign = block.valueVerticalAlign;

          if (field === 'hasSeparateValueFormatting' && !value && !blockUsesForcedValueFormatting) {
            // Copy label formatting to value formatting
            newValueFontName = block.labelFontName;
            newValueFontSize = block.labelFontSize;
            newValueFontWeight = block.labelFontWeight;
            newValueFontWidth = block.labelFontWidth;
            newValueAlign = block.labelAlign;
            newValueFontColor = block.labelFontColor;
            newValueVerticalAlign = block.labelVerticalAlign;
          } else if (field === 'labelFontName' && !blockHasSeparateValueFormatting) {
            // If separate formatting is disabled, update value formatting when label formatting changes
            newValueFontName = String(value);
          } else if (field === 'labelFontSize' && !blockHasSeparateValueFormatting) {
            newValueFontSize = Number(value);
          } else if (field === 'labelFontWeight' && !blockHasSeparateValueFormatting) {
            newValueFontWeight = Number(value);
          } else if (field === 'labelFontWidth' && !blockHasSeparateValueFormatting) {
            newValueFontWidth = Number(value);
          } else if (field === 'labelAlign' && !blockHasSeparateValueFormatting) {
            newValueAlign = String(value);
          } else if (field === 'labelFontColor' && !blockHasSeparateValueFormatting) {
            newValueFontColor = String(value);
          } else if (field === 'valueFontName') {
            newValueFontName = String(value);
          } else if (field === 'valueFontSize') {
            newValueFontSize = Number(value);
          } else if (field === 'valueFontWeight') {
            newValueFontWeight = Number(value);
          } else if (field === 'valueFontWidth') {
            newValueFontWidth = Number(value);
          } else if (field === 'valueAlign') {
            newValueAlign = String(value);
          } else if (field === 'valueFontColor') {
            newValueFontColor = String(value);
          } else if (field === "valueVerticalAlign") {
            newValueVerticalAlign = String(value);
          }

          // Create a new Block instance for the modified block
          const newBlock = Block.create({
            ...block,
            name: field === 'name' ? String(value) : block.name,
            shouldDisplayBlockName: field === 'shouldDisplayBlockName' ? Boolean(value) : block.shouldDisplayBlockName,
            blockNameFont: field === 'blockNameFont' ? String(value) : block.blockNameFont,
            blockNameSize: field === 'blockNameSize' ? Number(value) : block.blockNameSize,
            blockNameWeight: field === 'blockNameWeight' ? Number(value) : block.blockNameWeight,
            blockNameFontWidth: field === 'blockNameFontWidth' ? Number(value) : block.blockNameFontWidth,
            blockNameColor: field === 'blockNameColor' ? String(value) : block.blockNameColor,
            blockNameAlignment: field === 'blockNameAlignment' ? value as Block['blockNameAlignment'] : (block.blockNameAlignment ?? 'center'),
            numColumns: field === 'numColumns' ? Number(value) : block.numColumns,
            distanceBelowBlockName: field === 'distanceBelowBlockName' ? Number(value) : block.distanceBelowBlockName,
            labelValueGap: field === 'labelValueGap' ? Number(value) : block.labelValueGap,
            marginColumn: field === 'marginColumn' ? Number(value) : (block.marginColumn ?? 45),
            edgeDistance: field === 'edgeDistance' ? Number(value) : (block.edgeDistance ?? 0),
            distanceBetweenRows: field === 'distanceBetweenRows' ? Number(value) : block.distanceBetweenRows,
            distanceFromPreviousBlock: field === 'distanceFromPreviousBlock' ? Number(value) : block.distanceFromPreviousBlock,
            isSongBlock: field === 'isSongBlock' ? Boolean(value) : block.isSongBlock,
            isSingleCell: field === 'isSingleCell' ? Boolean(value) : block.isSingleCell,
            isCreditsList: field === 'isCreditsList' ? Boolean(value) : block.isCreditsList,
            separator: field === 'separator' ? String(value) : block.separator,
            isLogoBlock: field === 'isLogoBlock' ? Boolean(value) : block.isLogoBlock,
            sbsBlocks: field === 'sbsBlocks' ? String(value) as Block['sbsBlocks'] : (block.sbsBlocks ?? 'normal'),
            sbsDistance: field === 'sbsDistance'
              ? (Number.isNaN(Number(value)) ? 0 : Number(value))
              : block.sbsDistance,
            labelFontName: field === 'labelFontName' ? String(value) : block.labelFontName,
            labelFontSize: field === 'labelFontSize' ? Number(value) : block.labelFontSize,
            labelFontWeight: field === 'labelFontWeight' ? Number(value) : block.labelFontWeight,
            labelFontWidth: field === 'labelFontWidth' ? Number(value) : block.labelFontWidth,
            labelAlign: field === 'labelAlign' ? String(value) : block.labelAlign,
            labelVerticalAlign: field === 'labelVerticalAlign' ? String(value) : block.labelVerticalAlign,
            labelFontColor: field === 'labelFontColor' ? String(value) : block.labelFontColor,
            valueFontName: newValueFontName,
            valueFontSize: newValueFontSize,
            valueFontWeight: newValueFontWeight,
            valueFontWidth: newValueFontWidth,
            valueAlign: newValueAlign,
            valueVerticalAlign: newValueVerticalAlign,
            valueFontColor: newValueFontColor,
            hasSeparateValueFormatting: newHasSeparateValueFormatting,
            labelValueAlignment: field === 'labelValueAlignment' ? String(value) as Block['labelValueAlignment'] : block.labelValueAlignment,
            exchangeLabelValue: field === 'exchangeLabelValue' ? Boolean(value) : block.exchangeLabelValue,
            cardFadeInFrames: field === 'cardFadeInFrames' ? parseFrameValue(value) : (block.cardFadeInFrames ?? 0),
            cardDurationFullyUpFrames: field === 'cardDurationFullyUpFrames' ? parseFrameValue(value) : (block.cardDurationFullyUpFrames ?? 0),
            cardFadeOutFrames: field === 'cardFadeOutFrames' ? parseFrameValue(value) : (block.cardFadeOutFrames ?? 0),
            cardSlugFrames: field === 'cardSlugFrames' ? parseFrameValue(value) : (block.cardSlugFrames ?? 0),
            cardOverrideFullWidth: field === 'cardOverrideFullWidth' ? Boolean(value) : (block.cardOverrideFullWidth ?? false),
            isLocked: field === 'isLocked' ? Boolean(value) : block.isLocked,
            dataRows: block.dataRows // CRITICAL: Preserve dataRows
          });
          return newBlock;
        }
        return block;
      });
      console.log('BlockConfigForm: handleBlockChange, updatedBlocks:', updatedBlocks);
      return updatedBlocks;
    });
  };

  // Toggle pane collapsed state
  const togglePane = (pane: string) => {
    trackEvent('block_config_section_toggled', {
      project_id: selectedProject?.id,
      section_name: pane,
      next_collapsed: !collapsedPanes[pane],
    });
    setCollapsedPanes({
      ...collapsedPanes,
      [pane]: !collapsedPanes[pane]
    });
  };

  // Get the active block
  const activeBlock = blocks[activeBlockIndex];
  const activeBlockUsesForcedValueFormatting: boolean = isPlainSingleCellBlock(activeBlock);
  const shouldShowSeparateValueFormattingToggle: boolean = Boolean(activeBlock && !activeBlockUsesForcedValueFormatting);
  const shouldShowValueFormattingControls: boolean = Boolean(activeBlockUsesForcedValueFormatting || activeBlock?.hasSeparateValueFormatting);
  const getFontVariableConfig = (fontName: string | undefined): VariableFontConfig | undefined => {
    if (!fontName) {
      return undefined;
    }
    const variableFontConfig: VariableFontConfig | undefined =
      fontOptions.find((fontOption: FontOption) => fontOption.value === fontName)?.variableFontConfig;
    return variableFontConfig ? normalizeVariableFontConfigSteps(variableFontConfig) : undefined;
  };
  const totalCardFrames: number =
    (activeBlock?.cardFadeInFrames ?? 0) +
    (activeBlock?.cardDurationFullyUpFrames ?? 0) +
    (activeBlock?.cardFadeOutFrames ?? 0) +
    (activeBlock?.cardSlugFrames ?? 0);
  const totalCardSeconds: number = fps > 0 ? totalCardFrames / fps : 0;
  const totalCardTimeLabel: string = fps > 0
    ? `${totalCardFrames} ${t`frames`} (${totalCardSeconds.toFixed(2)} ${t`seconds`})`
    : `${totalCardFrames} ${t`frames`} (${t`invalid fps`})`;

  const handleBlockTypeChange = (index: number, blockType: BlockType) => {
    const blockForAnalytics: Block | undefined = blocks[index] ?? existingBlocks[index];
    trackEvent('block_type_changed', {
      project_id: selectedProject?.id,
      block_id: blockForAnalytics?.id,
      block_name: blockForAnalytics?.name,
      previous_type: getBlockType(blockForAnalytics),
      next_type: blockType,
    });
    setBlocks(prevBlocks => prevBlocks.map((block, i) => {
      if (i !== index) return block;
      const isSingleCellBlockType: boolean = blockType === 'single-cell';
      return Block.create({
        ...block,
        isSongBlock: blockType === 'song',
        isSingleCell: blockType === 'song' || blockType === 'credits' || isSingleCellBlockType,
        isCreditsList: blockType === 'credits',
        isLogoBlock: blockType === 'logo',
        hasSeparateValueFormatting: isSingleCellBlockType ? true : block.hasSeparateValueFormatting,
        dataRows: block.dataRows,
      });
    }));
  };


  return (
    <>
      <h2>{t`Block Properties`}                 <button
        className={`block-lock-btn${activeBlock?.isLocked ? ' locked' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          handleBlockChange(activeBlockIndex, 'isLocked', !(activeBlock?.isLocked));
        }}
        title={activeBlock?.isLocked ? t`Unlock block` : t`Lock block`}
        aria-label={activeBlock?.isLocked ? t`Unlock block` : t`Lock block`}
      >
        {activeBlock?.isLocked ? '🔒' : '🔓'}
      </button>
      </h2>
      <div className="block-config-form">
        {isCardsMode && activeBlock && (
          <div className={`config-pane ${collapsedPanes.card ? 'collapsed' : ''}`}>
            <h3 className="pane-title" onClick={() => togglePane('card')}>
              {t`Card Settings`}
            </h3>
            <div className="pane-content">
              <div className="form-group">
                <label>{t`Fade In`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={activeBlock.cardFadeInFrames ?? 0}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardFadeInFrames', e.target.value)}
                  />
                  <span className="unit">{t`frames`}</span>
                </div>
              </div>
              <div className="form-group">
                <label>{t`Duration fully up`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={activeBlock.cardDurationFullyUpFrames ?? 0}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardDurationFullyUpFrames', e.target.value)}
                  />
                  <span className="unit">{t`frames`}</span>
                </div>
              </div>
              <div className="form-group">
                <label>{t`Fade out`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={activeBlock.cardFadeOutFrames ?? 0}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardFadeOutFrames', e.target.value)}
                  />
                  <span className="unit">{t`frames`}</span>
                </div>
              </div>
              <div className="form-group">
                <label>{t`Slug`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={activeBlock.cardSlugFrames ?? 0}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardSlugFrames', e.target.value)}
                  />
                  <span className="unit">{t`frames`}</span>
                </div>
              </div>
              <div className="form-group">
                <label>{t`Full time`}</label>
                <div className="input-wrapper">
                  <span>{totalCardTimeLabel}</span>
                </div>
              </div>
              <div className="form-group">
                <label>{t`Override to full width`}</label>
                <div className="input-wrapper">
                  <input
                    type="checkbox"
                    checked={activeBlock.cardOverrideFullWidth ?? false}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'cardOverrideFullWidth', e.target.checked)}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Block Layout Properties */}
        <div className={`config-pane ${collapsedPanes.layout ? 'collapsed' : ''}`}>
          <h3 className="pane-title" onClick={() => togglePane('layout')}>
            {t`Block Layout`}
          </h3>

          <div className="pane-content">
            <div className="form-group">
              <label>{t`Block Type`}</label>
              <div className="input-wrapper">
                <Select
                  className="dropdown-menu-select"
                  value={activeBlock ? getBlockType(activeBlock) : 'role-name'}
                  onChange={(e) => handleBlockTypeChange(activeBlockIndex, e.target.value as BlockType)}
                  style={{ width: '130px', height: '34px' }}
                >
                  <MenuItem value="role-name">{t`Role → Name`}</MenuItem>
                  <MenuItem value="single-cell">{t`Single Cell`}</MenuItem>
                  <MenuItem value="song">{t`Song`}</MenuItem>
                  <MenuItem value="credits">{t`Credits List`}</MenuItem>
                  <MenuItem value="logo">{t`Logo`}</MenuItem>
                </Select>
              </div>
            </div>
            {activeBlock && getBlockType(activeBlock) === 'credits' && (
              <div className="form-group">
                <label>{t`Separator`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.separator || '•'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'separator', e.target.value as string)}
                    style={{ width: '80px', height: '34px' }}
                  >
                    <MenuItem value="|">|</MenuItem>
                    <MenuItem value="•">•</MenuItem>
                    <MenuItem value=",">,</MenuItem>
                    <MenuItem value="-">-</MenuItem>
                    <MenuItem value="/">/</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            <div className="form-group">
              <label>{t`Display Block Name`}</label>
              <div className="input-wrapper">
                <input
                  type="checkbox"
                  checked={activeBlock?.shouldDisplayBlockName}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'shouldDisplayBlockName', e.target.checked)}
                />
              </div>
            </div>
            {activeBlock?.shouldDisplayBlockName && (
              <div className="form-group">
                <label>{t`Block Name Alignment`}</label>
                <div className="input-wrapper">
                  <Select
                    className="block-align-select"
                    value={activeBlock?.blockNameAlignment ?? 'center'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'blockNameAlignment', e.target.value)}
                    sx={{
                      fontSize: '14px',
                      width: '92px',
                      height: '34px',
                      '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                      '.MuiSelect-icon': { color: '#007CB2' }
                    }}
                  >
                    <MenuItem value="left">{t`Left`}</MenuItem>
                    <MenuItem value="center">{t`Center`}</MenuItem>
                    <MenuItem value="right">{t`Right`}</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            {/* block name font */}
            {activeBlock?.shouldDisplayBlockName && (
              <>
                <div className="form-group">
                  <label>{t`Block Name Font`}</label>
                  <div className="input-wrapper">
                    <ReusableFontPicker
                      value={activeBlock?.blockNameFont}
                      options={fontOptions}
                      onChange={(nextFont: string) =>
                        handleBlockChange(activeBlockIndex, "blockNameFont", nextFont)
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t`Block Name Font Size`}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      min="6"
                      max="72"
                      value={activeBlock?.blockNameSize}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'blockNameSize', parseInt(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                <FontWeightWidthControls
                  fontName={activeBlock?.blockNameFont}
                  weight={activeBlock?.blockNameWeight}
                  width={activeBlock?.blockNameFontWidth}
                  weightField="blockNameWeight"
                  widthField="blockNameFontWidth"
                  onChange={(field, nextValue) => handleBlockChange(activeBlockIndex, field, nextValue)}
                  weightLabel={t`Block Name Font Weight`}
                  widthLabel={t`Width`}
                  variableFontConfig={getFontVariableConfig(activeBlock?.blockNameFont)}
                />

                <div className="form-group">
                  <label>{t`Block Name Font Color`}</label>
                  <div className="input-wrapper color-input-wrapper">
                    <ColorPicker
                      value={activeBlock?.blockNameColor ?? '#ffffff'}
                      onChange={(color) => handleBlockChange(activeBlockIndex, 'blockNameColor', color)}
                      showValue
                    />
                  </div>
                </div>
              </>
            )}
            <div className="form-group">
              <label>{t`Number of Columns`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="1"
                  value={activeBlock?.numColumns}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'numColumns', parseInt(e.target.value))}
                />
              </div>
            </div>
            {!activeBlock?.isLogoBlock && (
              <div className="form-group">
                <label>{t`Side-by-Side Block`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.sbsBlocks ?? 'normal'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'sbsBlocks', e.target.value)}
                    MenuProps={{
                      PaperProps: { className: "block-font-menu" }
                    }}
                  >
                    <MenuItem value="normal">{t`Normal`}</MenuItem>
                    <MenuItem value="left">{t`Left`}</MenuItem>
                    <MenuItem value="right">{t`Right`}</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            {activeBlock?.sbsBlocks === 'left' && (
              <div className="form-group">
                <label>{t`Side by Side Distance`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    value={activeBlock?.sbsDistance ?? 0}
                    onChange={(e) =>
                      handleBlockChange(activeBlockIndex, 'sbsDistance', parseInt(e.target.value))
                    }
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}
            {activeBlock?.shouldDisplayBlockName && (
            <div className="form-group">
              <label>{t`Distance Below Block Name`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.distanceBelowBlockName}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBelowBlockName', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>
            )}
            {!activeBlock?.isLogoBlock && (!activeBlock?.isSingleCell || activeBlock?.isSongBlock) && (
              <>
                <div className="form-group">
                  <label>{t`Exchange Label/Value`}</label>
                  <div className="input-wrapper">
                    <input
                      type="checkbox"
                      checked={activeBlock?.exchangeLabelValue ?? false}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'exchangeLabelValue', e.target.checked)}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>{ activeBlock?.isSongBlock ? t`Song Value Row Distance` : t`Label/Value Gap`}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      min="0"
                      value={activeBlock?.labelValueGap}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueGap', parseInt(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>
              </>
            )}
            {!activeBlock?.isLogoBlock && !activeBlock?.isSongBlock && (
              <div className="form-group">
                <label>{t`Label/Value Alignment`}</label>
                <div className="input-wrapper">
                  <Select
                    className="dropdown-menu-select"
                    value={activeBlock?.labelValueAlignment ?? 'horizontal'}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'labelValueAlignment', e.target.value)}
                    style={{ width: '120px', height: '34px' }}
                  >
                    <MenuItem value="horizontal">{t`Horizontal`}</MenuItem>
                    <MenuItem value="vertical">{t`Vertical`}</MenuItem>
                  </Select>
                </div>
              </div>
            )}
            {activeBlock?.numColumns > 1 && !activeBlock?.isLogoBlock && (
              <div className="form-group">
                <label>{t`Distance Between Columns`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="0"
                    value={activeBlock?.marginColumn ?? 45}
                    onChange={(e) => handleBlockChange(activeBlockIndex, 'marginColumn', parseInt(e.target.value))}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>{ activeBlock?.isSongBlock ? t`Song Title/Details Distance` : t`Distance Between Rows`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.distanceBetweenRows}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceBetweenRows', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>
            {activeBlock?.isSingleCell && !activeBlock?.isLogoBlock && (
              <div className="form-group">
                <label>{t`Edge Distance`}</label>
                <div className="input-wrapper">
                  <input
                    type="number"
                    min="-20"
                    max="80"
                    value={activeBlock?.edgeDistance ?? 0}
                    onChange={(e) => {
                      const parsedValue: number = Number.parseInt(e.target.value, 10);
                      handleBlockChange(
                        activeBlockIndex,
                        'edgeDistance',
                        Number.isNaN(parsedValue) ? 0 : parsedValue
                      );
                    }}
                  />
                  <span className="unit">px</span>
                </div>
              </div>
            )}
            <div className="form-group">
              <label>{t`Distance from Previous Block`}</label>
              <div className="input-wrapper">
                <input
                  type="number"
                  min="0"
                  value={activeBlock?.distanceFromPreviousBlock}
                  onChange={(e) => handleBlockChange(activeBlockIndex, 'distanceFromPreviousBlock', parseInt(e.target.value))}
                />
                <span className="unit">px</span>
              </div>
            </div>

          </div>
        </div>

        {/* Label Formatting (when NOT Logo Block or plain Single Cell) */}
        {!activeBlock?.isLogoBlock && (!activeBlock?.isSingleCell || activeBlock?.isSongBlock || activeBlock?.isCreditsList) && (
            <div className={`config-pane ${collapsedPanes.label ? 'collapsed' : ''}`}>
              <h3 className="pane-title" onClick={() => togglePane('label')}>
                {t`Label Formatting`}
              </h3>
              <div className="pane-content">
                <div className="form-group">
                  <label>{t`Font`}</label>
                  <div className="input-wrapper">
                    <ReusableFontPicker
                      value={activeBlock?.labelFontName}
                      options={fontOptions}
                      onChange={(nextFont: string) =>
                        handleBlockChange(activeBlockIndex, "labelFontName", nextFont)
                      }
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t`Font Size`}</label>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      min="6"
                      max="72"
                      value={activeBlock?.labelFontSize}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'labelFontSize', parseInt(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                <FontWeightWidthControls
                  fontName={activeBlock?.labelFontName}
                  weight={activeBlock?.labelFontWeight}
                  width={activeBlock?.labelFontWidth}
                  weightField="labelFontWeight"
                  widthField="labelFontWidth"
                  onChange={(field, nextValue) => handleBlockChange(activeBlockIndex, field, nextValue)}
                  weightLabel={t`Font Weight`}
                  widthLabel={t`Width`}
                  variableFontConfig={getFontVariableConfig(activeBlock?.labelFontName)}
                />

                <div className="form-group">
                  <label>{t`Alignment`}</label>
                  <div className="input-wrapper">
                    <Select
                      className="block-align-select"
                      value={activeBlock?.labelAlign ?? 'right'}
                      onChange={(e) => handleBlockChange(activeBlockIndex, 'labelAlign', e.target.value)}
                      sx={{
                        fontSize: '14px',
                        width: '92px',
                        height: '34px',
                        '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                        '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                      }}
                    >
                      <MenuItem value="left">{t`Left`}</MenuItem>
                      <MenuItem value="center">{t`Center`}</MenuItem>
                      <MenuItem value="right">{t`Right`}</MenuItem>
                    </Select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t`Vertical Align`}</label>
                  <div className="input-wrapper">
                    <Select
                      className="block-align-select"
                      value={activeBlock?.labelVerticalAlign ?? "bottom"}
                      onChange={(e) => handleBlockChange(activeBlockIndex, "labelVerticalAlign", e.target.value)}
                      sx={{
                        fontSize: '14px',
                        width: '92px',
                        height: '34px',
                        '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                        '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                      }}
                    >
                      <MenuItem value="top">{t`Top`}</MenuItem>
                      <MenuItem value="center">{t`Center`}</MenuItem>
                      <MenuItem value="bottom">{t`Bottom`}</MenuItem>
                    </Select>
                  </div>
                </div>

                <div className="form-group">
                  <label>{t`Font Color`}</label>
                  <div className="input-wrapper color-input-wrapper">
                    <ColorPicker
                      value={activeBlock?.labelFontColor ?? '#ffffff'}
                      onChange={(color) => handleBlockChange(activeBlockIndex, 'labelFontColor', color)}
                      showValue
                    />
                  </div>
                </div>
              </div>
            </div>
        )}

        {/* Value Formatting Properties (when NOT Logo Block) */}
        {!activeBlock?.isLogoBlock && (
            <div className={`config-pane ${collapsedPanes.value ? 'collapsed' : ''}`}>
              <h3 className="pane-title" onClick={() => togglePane('value')}>
                {t`Value Formatting`}
              </h3>
              <div className="pane-content">
                {/* Toggle for separate value formatting */}
                {shouldShowSeparateValueFormattingToggle && (
                  <div className="form-group" style={{ marginLeft: "-11px", display: "flex", flexDirection: "row", alignItems: "center", whiteSpace: "nowrap", }}>
                    <label style={{ marginLeft: "12px" }}>{t`Separate formatting for values?`}</label>
                    <input
                      type="checkbox"
                      style={{ marginLeft: "8px" }}
                      checked={activeBlock?.hasSeparateValueFormatting}
                      onChange={(e) =>
                        handleBlockChange(activeBlockIndex, 'hasSeparateValueFormatting', e.target.checked)
                      }
                    />
                  </div>
                )}

                {/* Value formatting options - only show if separate formatting is enabled */}
                {shouldShowValueFormattingControls && (
                  <>
                    <div className="form-group">
                      <label>{t`Font`}</label>
                      <div className="input-wrapper">
                        <ReusableFontPicker
                          value={activeBlock?.valueFontName}
                          options={fontOptions}
                          onChange={(nextFont: string) =>
                            handleBlockChange(activeBlockIndex, "valueFontName", nextFont)
                          }
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t`Font Size`}</label>
                      <div className="input-wrapper">
                        <input
                          type="number"
                          min="6"
                          max="72"
                          value={activeBlock?.valueFontSize}
                          onChange={(e) => handleBlockChange(activeBlockIndex, 'valueFontSize', parseInt(e.target.value))}
                        />
                        <span className="unit">px</span>
                      </div>
                    </div>

                    <FontWeightWidthControls
                      fontName={activeBlock?.valueFontName}
                      weight={activeBlock?.valueFontWeight}
                      width={activeBlock?.valueFontWidth}
                      weightField="valueFontWeight"
                      widthField="valueFontWidth"
                      onChange={(field, nextValue) => handleBlockChange(activeBlockIndex, field, nextValue)}
                      weightLabel={t`Font Weight`}
                      widthLabel={t`Width`}
                      variableFontConfig={getFontVariableConfig(activeBlock?.valueFontName)}
                    />

                    <div className="form-group">
                      <label>{t`Alignment`}</label>
                      <div className="input-wrapper">
                        <Select
                          className="block-align-select"
                          value={activeBlock?.valueAlign ?? 'right'}
                          onChange={(e) => handleBlockChange(activeBlockIndex, 'valueAlign', e.target.value)}
                          sx={{
                            fontSize: '14px',
                            width: '92px',
                            height: '34px',
                            '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                            '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                          }}
                          MenuProps={{
                            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                            transformOrigin: { vertical: 'top', horizontal: 'left' },
                            PaperProps: { style: { marginTop: '18px' } }
                          }}
                        >
                          <MenuItem value="left">{t`Left`}</MenuItem>
                          <MenuItem value="center">{t`Center`}</MenuItem>
                          <MenuItem value="right">{t`Right`}</MenuItem>
                        </Select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t`Vertical Align`}</label>
                      <div className="input-wrapper">
                        <Select
                          className="block-align-select"
                          value={activeBlock?.valueVerticalAlign ?? "bottom"}
                          onChange={(e) => handleBlockChange(activeBlockIndex, "valueVerticalAlign", e.target.value)}
                          sx={{
                            fontSize: '14px',
                            width: '92px',
                            height: '34px',
                            '.MuiSelect-select': { textAlign: 'left', display: 'flex', alignItems: 'center', paddingLeft: '10px' },
                            '.MuiSelect-icon': { color: '#007CB2' } // arrow color
                          }}
                        >
                          <MenuItem value="top">{t`Top`}</MenuItem>
                          <MenuItem value="center">{t`Center`}</MenuItem>
                          <MenuItem value="bottom">{t`Bottom`}</MenuItem>
                        </Select>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>{t`Font Color`}</label>
                      <div className="input-wrapper color-input-wrapper">
                        <ColorPicker
                          value={activeBlock?.valueFontColor ?? '#ffffff'}
                          onChange={(color) => handleBlockChange(activeBlockIndex, 'valueFontColor', color)}
                          showValue
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
        )}
      </div>
    </>
  );
};

export default BlockConfigForm;
